import React, { useState, useEffect, useCallback } from 'react';
import { getTimeEntries, createTimeEntry, getTimeSummary, exportTimePDF, getEmployeeHoursSummary, getTimeChangeRequests, createTimeChangeRequest, deleteTimeChangeRequest, getTimeBalance, getProxyUsers, createTimeRequestForOther } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Clock, Plus, Download, Calendar, Loader2, TrendingUp, TrendingDown, CheckCircle2, AlertCircle, Edit, FileText, XCircle, PenLine, RefreshCw, Users } from 'lucide-react';

export const TimeTrackingPage = () => {
    const { user } = useAuth();
    const [entries, setEntries] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [summary, setSummary] = useState(null);
    const [hoursSummary, setHoursSummary] = useState(null);
    const [timeBalance, setTimeBalance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    
    // Dialog states
    const [requestDialogOpen, setRequestDialogOpen] = useState(false);
    const [vacationDialogOpen, setVacationDialogOpen] = useState(false);
    const [directEntryDialogOpen, setDirectEntryDialogOpen] = useState(false);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [saving, setSaving] = useState(false);
    
    // All employees now use manual time entry (Terminal was removed)
    // The old "isOfficeWorker" logic is no longer needed
    const canEnterTimeDirectly = true; // All employees can now submit time requests
    
    // Direct time entry form (now for all employees via request system)
    const [directEntryForm, setDirectEntryForm] = useState({
        date: new Date().toISOString().split('T')[0],
        start_time: '08:00',
        end_time: '17:00',
        break_minutes: 30
    });
    
    // Request form
    const [requestForm, setRequestForm] = useState({
        request_type: 'NEW',
        date: new Date().toISOString().split('T')[0],
        start_time: '08:00',
        end_time: '17:00',
        break_minutes: 30,
        reason: '',
        alsoForProxy: false  // Also submit same time for proxy user (Michael)
    });
    
    // Vacation form
    const [vacationForm, setVacationForm] = useState({
        date: new Date().toISOString().split('T')[0],
        entry_type: 'VACATION'
    });
    
    // Proxy users (users this employee can submit time for)
    const [proxyUsers, setProxyUsers] = useState([]);

    const loadProxyUsers = useCallback(async () => {
        try {
            const res = await getProxyUsers();
            setProxyUsers(res.data || []);
        } catch {
            // Silently fail - proxy users are optional
        }
    }, []);

    const loadTimeBalance = useCallback(async () => {
        try {
            const res = await getTimeBalance();
            setTimeBalance(res.data);
        } catch {
            // Silently fail
        }
    }, []);

    const loadHoursSummary = useCallback(async () => {
        try {
            const res = await getEmployeeHoursSummary(user.id);
            setHoursSummary(res.data);
        } catch {
            // Silently fail
        }
    }, [user.id]);

    const loadMyRequests = useCallback(async () => {
        try {
            const res = await getTimeChangeRequests();
            setMyRequests(res.data);
        } catch {
            // Silently fail
        }
    }, []);

    const loadData = useCallback(async () => {
        try {
            const [entriesRes, summaryRes] = await Promise.all([
                getTimeEntries({ month: selectedMonth, year: selectedYear }),
                getTimeSummary(selectedMonth, selectedYear)
            ]);
            setEntries(entriesRes.data);
            setSummary(summaryRes.data);
        } catch (err) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [selectedMonth, selectedYear]);

    // Load data when month/year changes
    useEffect(() => {
        loadData();
        loadHoursSummary();
        loadMyRequests();
        loadTimeBalance();
        loadProxyUsers();
    }, [loadData, loadHoursSummary, loadMyRequests, loadTimeBalance, loadProxyUsers]);

    const handleRefresh = async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await Promise.all([loadData(), loadHoursSummary(), loadMyRequests(), loadTimeBalance(), loadProxyUsers()]);
            toast.success('Aktualisiert');
        } catch (err) {
            toast.error('Fehler beim Aktualisieren');
        } finally {
            setRefreshing(false);
        }
    };

    // Open request dialog for new entry
    const openNewRequestDialog = () => {
        setSelectedEntry(null);
        setRequestForm({
            request_type: 'NEW',
            date: new Date().toISOString().split('T')[0],
            start_time: '08:00',
            end_time: '17:00',
            break_minutes: 30,
            reason: ''
        });
        setRequestDialogOpen(true);
    };

    // Open request dialog for editing existing entry
    const openEditRequestDialog = (entry) => {
        setSelectedEntry(entry);
        setRequestForm({
            request_type: 'EDIT',
            date: entry.date,
            start_time: entry.start_time || '08:00',
            end_time: entry.end_time || '17:00',
            break_minutes: entry.break_minutes || 0,
            reason: ''
        });
        setRequestDialogOpen(true);
    };

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            // Submit for current user (Luca)
            await createTimeChangeRequest({
                request_type: requestForm.request_type,
                original_entry_id: selectedEntry?.id || null,
                date: requestForm.date,
                start_time: requestForm.start_time,
                end_time: requestForm.end_time,
                break_minutes: requestForm.break_minutes,
                reason: requestForm.reason.trim() || 'Tägliche Arbeitszeit'
            });
            
            // Also submit for proxy user (Michael) if checkbox is checked
            if (requestForm.alsoForProxy && proxyUsers.length > 0) {
                const proxyUser = proxyUsers[0];
                await createTimeRequestForOther(proxyUser.id, {
                    request_type: 'NEW',
                    date: requestForm.date,
                    start_time: requestForm.start_time,
                    end_time: requestForm.end_time,
                    break_minutes: requestForm.break_minutes,
                    reason: requestForm.reason.trim() || `Eingereicht von ${user.name}`
                });
                toast.success(`Arbeitszeit für Sie und ${proxyUser.name} eingereicht`);
            } else {
                toast.success('Arbeitszeit eingereicht - wartet auf Genehmigung');
            }
            
            setRequestDialogOpen(false);
            setRequestForm(prev => ({ ...prev, alsoForProxy: false }));
            loadMyRequests();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Einreichen');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteRequest = async (requestId) => {
        if (!confirm('Antrag wirklich zurückziehen?')) return;
        try {
            await deleteTimeChangeRequest(requestId);
            toast.success('Antrag zurückgezogen');
            loadMyRequests();
        } catch (err) {
            toast.error('Fehler beim Löschen');
        }
    };

    // Direct time entry (for office workers - no approval needed)
    const openDirectEntryDialog = () => {
        setDirectEntryForm({
            date: new Date().toISOString().split('T')[0],
            start_time: '08:00',
            end_time: '17:00',
            break_minutes: 30
        });
        setDirectEntryDialogOpen(true);
    };

    const handleSubmitDirectEntry = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await createTimeEntry({
                date: directEntryForm.date,
                start_time: directEntryForm.start_time,
                end_time: directEntryForm.end_time,
                break_minutes: parseInt(directEntryForm.break_minutes) || 0,
                entry_type: 'WORK'
            });
            toast.success('Arbeitszeit erfasst');
            setDirectEntryDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    // Vacation/Sick entry
    const openVacationDialog = () => {
        setVacationForm({
            date: new Date().toISOString().split('T')[0],
            entry_type: 'VACATION'
        });
        setVacationDialogOpen(true);
    };

    const handleSubmitVacation = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await createTimeEntry({
                date: vacationForm.date,
                entry_type: vacationForm.entry_type,
                notes: ''
            });
            toast.success(
                vacationForm.entry_type === 'VACATION' ? 'Urlaubstag eingetragen' : 
                vacationForm.entry_type === 'SICK' ? 'Krankheitstag eingetragen' : 
                'Feiertag eingetragen'
            );
            setVacationDialogOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleExportPDF = async () => {
        try {
            const response = await exportTimePDF(selectedMonth, selectedYear);
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Zeiterfassung_${selectedMonth}_${selectedYear}.html`;
            a.click();
            window.URL.revokeObjectURL(url);
            toast.success('Export erstellt');
        } catch (err) {
            toast.error('Fehler beim Export');
        }
    };

    const months = [
        { value: 1, label: 'Januar' },
        { value: 2, label: 'Februar' },
        { value: 3, label: 'März' },
        { value: 4, label: 'April' },
        { value: 5, label: 'Mai' },
        { value: 6, label: 'Juni' },
        { value: 7, label: 'Juli' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'Oktober' },
        { value: 11, label: 'November' },
        { value: 12, label: 'Dezember' },
    ];

    const entryTypeLabels = {
        'WORK': 'Arbeit',
        'VACATION': 'Urlaub',
        'SICK': 'Krank',
        'HOLIDAY': 'Feiertag'
    };

    const requestStatusLabels = {
        'PENDING': { label: 'Offen', class: 'bg-amber-100 text-amber-700' },
        'APPROVED': { label: 'Genehmigt', class: 'bg-emerald-100 text-emerald-700' },
        'REJECTED': { label: 'Abgelehnt', class: 'bg-red-100 text-red-700' }
    };

    const pendingRequests = myRequests.filter(r => r.status === 'PENDING');

    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center min-h-screen">
                <div className="spinner" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-6 pb-24" data-testid="time-tracking-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
                        Meine Zeiten
                        <button 
                            onClick={handleRefresh}
                            disabled={refreshing}
                            className="p-2 rounded-full hover:bg-muted transition-colors"
                            title="Aktualisieren"
                        >
                            <RefreshCw size={20} className={`text-muted-foreground ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                    </h1>
                    <p className="text-muted-foreground">Übersicht Ihrer Arbeitszeiten</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button onClick={openNewRequestDialog} data-testid="request-entry-btn">
                        <FileText className="h-4 w-4 mr-2" />
                        Zeit beantragen
                    </Button>
                    <Button variant="outline" onClick={openVacationDialog} data-testid="vacation-btn">
                        <Calendar className="h-4 w-4 mr-2" />
                        Urlaub/Krank
                    </Button>
                    <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
                        <Download className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                </div>
            </div>

            {/* Time Entry Card - All employees can now submit their daily hours */}
            <Card className="border-2 border-primary bg-primary/5">
                <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-center md:text-left">
                            <h2 className="text-lg font-semibold text-primary">Tägliche Arbeitszeit erfassen</h2>
                            <p className="text-muted-foreground text-sm">
                                Tragen Sie Ihre Arbeitszeiten ein. Nach Einreichung werden sie vom Admin geprüft.
                            </p>
                        </div>
                        <div className="flex gap-3 flex-wrap justify-center">
                            <Button onClick={openNewRequestDialog} className="bg-primary hover:bg-primary/90" data-testid="submit-daily-time-btn">
                                <PenLine className="h-5 w-5 mr-2" />
                                Zeit einreichen
                            </Button>
                            <Button variant="outline" onClick={() => setVacationDialogOpen(true)}>
                                <Calendar className="h-5 w-5 mr-2" />
                                Urlaub/Krank
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Pending Requests */}
            {pendingRequests.length > 0 && (
                <Card className="border-amber-300 bg-amber-50">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                            <Clock className="h-5 w-5" />
                            Offene Anträge ({pendingRequests.length})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-2">
                            {pendingRequests.map(req => (
                                <div key={req.id} className="flex items-center justify-between p-3 bg-white rounded border">
                                    <div>
                                        <p className="font-medium">
                                            {req.request_type === 'NEW' ? 'Neuer Eintrag' : 'Änderung'} - {new Date(req.date).toLocaleDateString('de-DE')}
                                        </p>
                                        <p className="text-sm text-muted-foreground">
                                            {req.start_time} - {req.end_time} ({req.break_minutes} Min Pause)
                                        </p>
                                        {req.reason && req.reason !== 'Tägliche Arbeitszeit' && (
                                            <p className="text-xs text-muted-foreground mt-1">Bemerkung: {req.reason}</p>
                                        )}
                                    </div>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        onClick={() => handleDeleteRequest(req.id)}
                                    >
                                        <XCircle className="h-4 w-4 text-destructive" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Weekly & Monthly Hours Summary */}
            {hoursSummary && hoursSummary.work_type === 'WEEKLY' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Weekly Status */}
                    <Card className={hoursSummary.weekly_fulfilled ? 'border-primary bg-primary/5' : 'border-amber-400 bg-amber-50'}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        Diese Woche ({hoursSummary.work_days_per_week || 5} Tage)
                                    </p>
                                    <p className="text-2xl font-bold">
                                        {hoursSummary.weekly_hours}h <span className="text-base font-normal text-muted-foreground">/ {hoursSummary.weekly_target}h</span>
                                    </p>
                                </div>
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${hoursSummary.weekly_fulfilled ? 'bg-primary text-white' : 'bg-amber-400 text-white'}`}>
                                    {hoursSummary.weekly_fulfilled ? (
                                        <CheckCircle2 className="h-6 w-6" />
                                    ) : (
                                        <AlertCircle className="h-6 w-6" />
                                    )}
                                </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                {hoursSummary.weekly_difference >= 0 ? (
                                    <>
                                        <TrendingUp className="h-4 w-4 text-primary" />
                                        <span className="text-sm text-primary font-medium">+{hoursSummary.weekly_difference}h Überstunden</span>
                                    </>
                                ) : (
                                    <>
                                        <TrendingDown className="h-4 w-4 text-amber-600" />
                                        <span className="text-sm text-amber-600 font-medium">{hoursSummary.weekly_difference}h noch offen</span>
                                    </>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Monthly Status */}
                    <Card className={hoursSummary.monthly_fulfilled_so_far ? 'border-primary bg-primary/5' : 'border-amber-400 bg-amber-50'}>
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Dieser Monat (bisher)</p>
                                    <p className="text-2xl font-bold">
                                        {hoursSummary.monthly_hours}h <span className="text-base font-normal text-muted-foreground">/ {hoursSummary.monthly_target_so_far}h</span>
                                    </p>
                                </div>
                                <div className={`h-12 w-12 rounded-full flex items-center justify-center ${hoursSummary.monthly_fulfilled_so_far ? 'bg-primary text-white' : 'bg-amber-400 text-white'}`}>
                                    {hoursSummary.monthly_fulfilled_so_far ? (
                                        <CheckCircle2 className="h-6 w-6" />
                                    ) : (
                                        <AlertCircle className="h-6 w-6" />
                                    )}
                                </div>
                            </div>
                            <div className="mt-2 flex items-center gap-2">
                                {hoursSummary.monthly_difference >= 0 ? (
                                    <>
                                        <TrendingUp className="h-4 w-4 text-primary" />
                                        <span className="text-sm text-primary font-medium">+{hoursSummary.monthly_difference}h im Plus</span>
                                    </>
                                ) : (
                                    <>
                                        <TrendingDown className="h-4 w-4 text-amber-600" />
                                        <span className="text-sm text-amber-600 font-medium">{hoursSummary.monthly_difference}h Rückstand</span>
                                    </>
                                )}
                            </div>
                            <p className="text-xs text-muted-foreground mt-2">
                                Monatsziel: {hoursSummary.monthly_target}h
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Hourly Worker Summary (no targets) */}
            {hoursSummary && hoursSummary.work_type === 'HOURLY' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border-amber-400 bg-amber-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Diese Woche</p>
                                    <p className="text-2xl font-bold">{hoursSummary.weekly_hours}h</p>
                                </div>
                                <div className="h-12 w-12 rounded-full flex items-center justify-center bg-amber-500 text-white">
                                    <Clock className="h-6 w-6" />
                                </div>
                            </div>
                            <p className="text-xs text-amber-600 mt-2">Stundenbasis - kein Soll definiert</p>
                        </CardContent>
                    </Card>
                    <Card className="border-amber-400 bg-amber-50">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">Dieser Monat</p>
                                    <p className="text-2xl font-bold">{hoursSummary.monthly_hours}h</p>
                                </div>
                                <div className="h-12 w-12 rounded-full flex items-center justify-center bg-amber-500 text-white">
                                    <Clock className="h-6 w-6" />
                                </div>
                            </div>
                            <p className="text-xs text-amber-600 mt-2">Stundenbasis - kein Soll definiert</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Time Balance Card - Only for WEEKLY employees */}
            {timeBalance && timeBalance.balance_hours !== null && (
                <Card className={`border-2 ${timeBalance.balance_hours >= 0 ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Zeitkonto {timeBalance.year}</p>
                                <p className={`text-3xl font-bold ${timeBalance.balance_hours >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {timeBalance.balance_formatted}
                                </p>
                            </div>
                            <div className={`h-12 w-12 rounded-full flex items-center justify-center ${timeBalance.balance_hours >= 0 ? 'bg-green-500' : 'bg-red-500'} text-white`}>
                                {timeBalance.balance_hours >= 0 ? <TrendingUp className="h-6 w-6" /> : <TrendingDown className="h-6 w-6" />}
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                            Soll: {timeBalance.total_expected}h | Ist: {timeBalance.total_actual}h
                        </p>
                        <p className="text-xs text-muted-foreground">
                            Stand: {new Date(timeBalance.as_of_date).toLocaleDateString('de-DE')}
                        </p>
                    </CardContent>
                </Card>
            )}

            {/* Month Summary */}
            <div className={`grid grid-cols-2 ${summary?.monthly_target ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-4`}>
                {summary?.monthly_target && (
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-3xl font-bold text-muted-foreground">{summary.monthly_target}</p>
                            <p className="text-sm text-muted-foreground">Monats-Soll</p>
                        </CardContent>
                    </Card>
                )}
                <Card className="bg-primary/5 border-primary">
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-primary">{summary?.monthly_actual || 0}</p>
                        <p className="text-sm text-muted-foreground font-medium">{summary?.monthly_target ? 'Monats-Ist' : 'Stunden'}</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-blue-600">{summary?.vacation_days || 0}</p>
                        <p className="text-sm text-muted-foreground">Urlaubstage</p>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4 text-center">
                        <p className="text-3xl font-bold text-red-600">{summary?.sick_days || 0}</p>
                        <p className="text-sm text-muted-foreground">Krankheitstage</p>
                    </CardContent>
                </Card>
            </div>

            {/* Month/Year Filter */}
            <div className="flex gap-4">
                <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                    <SelectTrigger className="w-[180px]" data-testid="month-select">
                        <Calendar className="h-4 w-4 mr-2" />
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {months.map(m => (
                            <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                    <SelectTrigger className="w-[120px]" data-testid="year-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {[2024, 2025, 2026, 2027].map(y => (
                            <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Entries Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg">Einträge {months.find(m => m.value === selectedMonth)?.label} {selectedYear}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {entries.length === 0 ? (
                        <p className="p-6 text-center text-muted-foreground">Keine Einträge für diesen Monat</p>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Datum</TableHead>
                                    <TableHead>Typ</TableHead>
                                    <TableHead>Beginn</TableHead>
                                    <TableHead>Ende</TableHead>
                                    <TableHead>Pause</TableHead>
                                    <TableHead className="text-right">Stunden</TableHead>
                                    <TableHead></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {entries.map((entry) => (
                                    <TableRow 
                                        key={entry.id}
                                        className={
                                            entry.entry_type === 'VACATION' ? 'bg-primary/5' :
                                            entry.entry_type === 'SICK' ? 'bg-red-50' :
                                            entry.entry_type === 'HOLIDAY' ? 'bg-amber-50' : ''
                                        }
                                    >
                                        <TableCell className="font-medium">
                                            {new Date(entry.date).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                entry.entry_type === 'WORK' ? 'bg-emerald-100 text-emerald-700' :
                                                entry.entry_type === 'VACATION' ? 'bg-primary/20 text-primary' :
                                                entry.entry_type === 'HOLIDAY' ? 'bg-amber-100 text-amber-700' :
                                                'bg-red-100 text-red-700'
                                            }`}>
                                                {entryTypeLabels[entry.entry_type]}
                                            </span>
                                        </TableCell>
                                        <TableCell>{entry.start_time || '-'}</TableCell>
                                        <TableCell>{entry.end_time || '-'}</TableCell>
                                        <TableCell>{entry.break_minutes || 0} Min</TableCell>
                                        <TableCell className="text-right font-semibold">{entry.total_hours?.toFixed(2) || '0.00'}</TableCell>
                                        <TableCell>
                                            {entry.entry_type === 'WORK' && (
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon" 
                                                    onClick={() => openEditRequestDialog(entry)}
                                                    title="Änderung beantragen"
                                                >
                                                    <Edit className="h-4 w-4 text-muted-foreground" />
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Request History */}
            {myRequests.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Meine Anträge</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Datum</TableHead>
                                    <TableHead>Typ</TableHead>
                                    <TableHead>Zeit</TableHead>
                                    <TableHead>Begründung</TableHead>
                                    <TableHead>Status</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {myRequests.map((req) => {
                                    const status = requestStatusLabels[req.status];
                                    return (
                                        <TableRow key={req.id}>
                                            <TableCell className="font-medium">
                                                {new Date(req.date).toLocaleDateString('de-DE')}
                                            </TableCell>
                                            <TableCell>
                                                {req.request_type === 'NEW' ? 'Neu' : 'Änderung'}
                                            </TableCell>
                                            <TableCell>
                                                {req.start_time} - {req.end_time}
                                            </TableCell>
                                            <TableCell className="max-w-[200px] truncate">
                                                {req.reason}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${status.class}`}>
                                                    {status.label}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Time Change Request Dialog */}
            <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
                <DialogContent data-testid="request-dialog">
                    <DialogHeader>
                        <DialogTitle>
                            {requestForm.request_type === 'NEW' ? 'Arbeitszeit einreichen' : 'Änderung beantragen'}
                        </DialogTitle>
                        <DialogDescription>
                            {requestForm.request_type === 'NEW' 
                                ? 'Tragen Sie Ihre Arbeitszeit für den gewählten Tag ein. Der Admin wird diese genehmigen.'
                                : 'Beantragen Sie eine Korrektur für diesen Eintrag.'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmitRequest} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Datum</Label>
                            <Input
                                type="date"
                                value={requestForm.date}
                                onChange={(e) => setRequestForm({...requestForm, date: e.target.value})}
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Beginn</Label>
                                <Input
                                    type="time"
                                    value={requestForm.start_time}
                                    onChange={(e) => setRequestForm({...requestForm, start_time: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ende</Label>
                                <Input
                                    type="time"
                                    value={requestForm.end_time}
                                    onChange={(e) => setRequestForm({...requestForm, end_time: e.target.value})}
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Pause (Minuten)</Label>
                            <Input
                                type="number"
                                value={requestForm.break_minutes}
                                onChange={(e) => setRequestForm({...requestForm, break_minutes: parseInt(e.target.value) || 0})}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Bemerkung (optional)</Label>
                            <Textarea
                                value={requestForm.reason}
                                onChange={(e) => setRequestForm({...requestForm, reason: e.target.value})}
                                placeholder="z.B. Normaler Arbeitstag, Außentermin, etc."
                                rows={2}
                            />
                        </div>
                        
                        {/* Checkbox for proxy user (e.g., Michael) */}
                        {proxyUsers.length > 0 && (
                            <div className="flex items-center space-x-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                <Checkbox
                                    id="alsoForProxy"
                                    checked={requestForm.alsoForProxy}
                                    onCheckedChange={(checked) => setRequestForm({...requestForm, alsoForProxy: checked})}
                                />
                                <label 
                                    htmlFor="alsoForProxy" 
                                    className="text-sm font-medium cursor-pointer flex items-center gap-2"
                                >
                                    <Users className="h-4 w-4 text-blue-600" />
                                    Auch für {proxyUsers[0].name} eintragen
                                </label>
                            </div>
                        )}
                        
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setRequestDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Arbeitszeit einreichen
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Vacation/Sick Dialog */}
            <Dialog open={vacationDialogOpen} onOpenChange={setVacationDialogOpen}>
                <DialogContent data-testid="vacation-dialog">
                    <DialogHeader>
                        <DialogTitle>Urlaub oder Krankheit eintragen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitVacation} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Typ</Label>
                            <Select value={vacationForm.entry_type} onValueChange={(v) => setVacationForm({...vacationForm, entry_type: v})}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="VACATION">Urlaub</SelectItem>
                                    <SelectItem value="SICK">Krank</SelectItem>
                                    <SelectItem value="HOLIDAY">Feiertag</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Datum</Label>
                            <Input
                                type="date"
                                value={vacationForm.date}
                                onChange={(e) => setVacationForm({...vacationForm, date: e.target.value})}
                                required
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setVacationDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Direct Time Entry Dialog (for office workers) */}
            <Dialog open={directEntryDialogOpen} onOpenChange={setDirectEntryDialogOpen}>
                <DialogContent data-testid="direct-entry-dialog">
                    <DialogHeader>
                        <DialogTitle>Arbeitszeit eintragen</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmitDirectEntry} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Datum</Label>
                            <Input
                                type="date"
                                value={directEntryForm.date}
                                onChange={(e) => setDirectEntryForm({...directEntryForm, date: e.target.value})}
                                required
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Beginn</Label>
                                <Input
                                    type="time"
                                    value={directEntryForm.start_time}
                                    onChange={(e) => setDirectEntryForm({...directEntryForm, start_time: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ende</Label>
                                <Input
                                    type="time"
                                    value={directEntryForm.end_time}
                                    onChange={(e) => setDirectEntryForm({...directEntryForm, end_time: e.target.value})}
                                    required
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Pause (Minuten)</Label>
                            <Input
                                type="number"
                                min="0"
                                value={directEntryForm.break_minutes}
                                onChange={(e) => setDirectEntryForm({...directEntryForm, break_minutes: e.target.value})}
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDirectEntryDialogOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={saving}>
                                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                                Speichern
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default TimeTrackingPage;
